import { apiClient } from './api';
import { revokeAllObjectUrls } from '../utils/objectUrlRegistry';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  teacher: {
    teacher_id: number;
    email: string;
    created_at: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export interface Teacher {
  teacher_id: number;
  email: string;
  created_at: string;
}

export interface RefreshTokenResponse {
  message: string;
  tokens: {
    accessToken: string;
  };
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  password: string;
}

export class AuthService {
  private currentUser: Teacher | null = null;

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
      
      // Store tokens from nested structure
      apiClient.setAuthTokens(response.tokens.accessToken, response.tokens.refreshToken);
      
      // Store user data
      this.currentUser = response.teacher;
      localStorage.setItem('current_user', JSON.stringify(response.teacher));
      
      return response;
    } catch (error) {
      this.currentUser = null;
      localStorage.removeItem('current_user');
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      // Call logout endpoint to invalidate refresh token
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local storage
      apiClient.clearAuth();
      revokeAllObjectUrls();
      this.currentUser = null;
      localStorage.removeItem('current_user');
    }
  }

  async refreshToken(): Promise<string> {
    const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh');
    return response.tokens.accessToken;
  }

  async getCurrentUser(): Promise<Teacher | null> {
    if (this.currentUser) {
      return this.currentUser;
    }

    // Try to get from localStorage
    const storedUser = localStorage.getItem('current_user');
    if (storedUser) {
      try {
        this.currentUser = JSON.parse(storedUser);
        return this.currentUser;
      } catch (error) {
        localStorage.removeItem('current_user');
      }
    }

    // If we have a token, try to get user from API
    if (apiClient.isAuthenticated()) {
      try {
        const user = await apiClient.get<Teacher>('/auth/me');
        this.currentUser = user;
        localStorage.setItem('current_user', JSON.stringify(user));
        return user;
      } catch (error) {
        // Token might be invalid
        this.logout();
        return null;
      }
    }

    return null;
  }

  async requestPasswordReset(email: string): Promise<void> {
    await apiClient.post('/auth/password-reset', { email });
  }

  async confirmPasswordReset(token: string, password: string): Promise<void> {
    await apiClient.post('/auth/password-reset/confirm', { token, password });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async updateProfile(updates: Partial<Pick<Teacher, 'email'>>): Promise<Teacher> {
    const updatedUser = await apiClient.patch<Teacher>('/auth/profile', updates);
    this.currentUser = updatedUser;
    localStorage.setItem('current_user', JSON.stringify(updatedUser));
    return updatedUser;
  }

  isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  getCurrentUserSync(): Teacher | null {
    if (this.currentUser) {
      return this.currentUser;
    }

    const storedUser = localStorage.getItem('current_user');
    if (storedUser) {
      try {
        this.currentUser = JSON.parse(storedUser);
        return this.currentUser;
      } catch (error) {
        localStorage.removeItem('current_user');
      }
    }

    return null;
  }

  // Utility methods
  getDisplayName(): string {
    const user = this.getCurrentUserSync();
    if (!user) return '';
    return user.email;
  }

  getInitials(): string {
    const user = this.getCurrentUserSync();
    if (!user) return '';
    const email = user.email;
    const atIndex = email.indexOf('@');
    const username = atIndex > 0 ? email.substring(0, atIndex) : email;
    return username.substring(0, 2).toUpperCase();
  }
}

export const authService = new AuthService();
export default authService;
